import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import getConfig from 'next/config';
import { PayPalScriptOptions } from '@paypal/paypal-js';
import { useFeatureValue } from '@monofront/business/contexts';
import {
  PAYPAL_4X_MAX_ELIGIBILITY,
  PAYPAL_4X_MIN_ELIGIBILITY,
} from '@monofront/business/constants';
import { useAuthentication } from '@context/businessContainer';
import { useSubscribeInCheckout } from '@monofront/business/hooks';
import {
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';

interface IPaypalContextProps {
  isReady: boolean;
  isMethodEligible: (amount: number) => boolean;
}

interface IPaypalProviderProps {
  children?: React.ReactNode;
  namespace: string;
  options?: Omit<
    PayPalScriptOptions,
    'currency' | 'dataClientMetadataId' | 'clientId'
  >;
}

const {
  publicRuntimeConfig: { paypalClientId },
} = getConfig();

const PaypalContext = createContext<IPaypalContextProps>({
  isReady: false,
  isMethodEligible: () => false,
});

const PaypalProviderInner = ({ children }) => {
  const paypal4xMin = useFeatureValue(...PAYPAL_4X_MIN_ELIGIBILITY);
  const paypal4xMax = useFeatureValue(...PAYPAL_4X_MAX_ELIGIBILITY);

  const [{ isResolved }] = usePayPalScriptReducer();
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);

  const isMethodEligible = useCallback(
    (amount: number) => {
      return (
        amount >= paypal4xMin &&
        amount <= paypal4xMax &&
        !isSubscriptionAvailable
      );
    },
    [paypal4xMin, paypal4xMax, isSubscriptionAvailable],
  );

  return (
    <PaypalContext.Provider
      value={useMemo(
        () => ({
          isReady: isResolved,
          isMethodEligible,
        }),
        [isResolved, isMethodEligible],
      )}
    >
      {children}
    </PaypalContext.Provider>
  );
};

const PaypalProvider = ({
  children,
  namespace,
  options,
}: IPaypalProviderProps) => {
  // The PayPalScriptProvider component doesn't react to options changes
  const paypalSdkOptions = useRef({
    ...options,
    clientId: paypalClientId as string,
    currency: 'EUR',
    dataNamespace: namespace,
    dataClientMetadataId: 'paypal-script',
  });

  return (
    <PayPalScriptProvider options={paypalSdkOptions.current}>
      <PaypalProviderInner>{children}</PaypalProviderInner>
    </PayPalScriptProvider>
  );
};

export default PaypalProvider;

export const usePaypalContext = () => useContext(PaypalContext);
